import { useEffect, type ReactNode } from "react";
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from "react-leaflet";
import { latLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";

// Brasil inteiro, para quando ainda não há ponto nenhum.
const CENTRO_BRASIL: [number, number] = [-14.2, -51.9];

export interface PontoMapa {
  /** Única e estável; mude-a para remontar o marcador (ex. ao trocar de métrica). */
  chave: string;
  lat: number;
  lng: number;
  /** Define o raio: área do círculo proporcional ao valor. */
  valor: number;
  tooltip: ReactNode;
}

interface MapaBolhasProps {
  /** Em ordem decrescente de valor: os maiores recebem os rótulos fixos. */
  pontos: PontoMapa[];
  cor: string;
  variante?: "padrao" | "tv";
  /** Modo TV: rótulo fixo nos N maiores pontos (não há mouse para o tooltip). */
  rotulosFixos?: number;
}

/** Mapa de bolhas por município (Silpi Farma e clientes da Home). */
export function MapaBolhas({ pontos, cor, variante = "padrao", rotulosFixos = 0 }: MapaBolhasProps) {
  const visiveis = pontos.filter((p) => p.valor > 0);
  const maximo = Math.max(1, ...visiveis.map((p) => p.valor));
  const tv = variante === "tv";

  return (
    <MapContainer
      center={CENTRO_BRASIL}
      zoom={4}
      scrollWheelZoom={!tv}
      zoomControl={!tv}
      attributionControl
      className="h-full w-full rounded-xl"
      style={{ background: tv ? "#0f172a" : undefined }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <AjustarAosPontos pontos={visiveis} />
      {/* Maiores por último: ficam por cima dos menores. */}
      {[...visiveis].reverse().map((ponto, i, lista) => {
        const posicao = lista.length - 1 - i;
        // Raio pela raiz: área do círculo proporcional ao volume.
        const raio = (tv ? 8 : 5) + (tv ? 32 : 20) * Math.sqrt(ponto.valor / maximo);
        return (
          <CircleMarker
            key={ponto.chave}
            center={[ponto.lat, ponto.lng]}
            radius={raio}
            pathOptions={{ color: cor, fillColor: cor, fillOpacity: 0.45, weight: 1.5 }}
          >
            <Tooltip permanent={posicao < rotulosFixos} direction="top" className={tv ? "!text-base" : undefined}>
              {ponto.tooltip}
            </Tooltip>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

/** Enquadra os pontos; refaz só quando o conjunto de pontos muda. */
function AjustarAosPontos({ pontos }: { pontos: PontoMapa[] }) {
  const map = useMap();
  // String estável: o array muda de identidade a cada refetch no modo TV, e
  // reenquadrar o mapa toda vez seria irritante. Só reenquadra se os pontos mudarem.
  const chave = pontos
    .map((p) => `${p.lat}:${p.lng}`)
    .sort()
    .join(";");

  useEffect(() => {
    if (!chave) return;
    const coordenadas = chave.split(";").map((p) => p.split(":").map(Number) as [number, number]);
    if (coordenadas.length === 1) {
      map.setView(coordenadas[0]!, 9);
      return;
    }
    map.fitBounds(latLngBounds(coordenadas), { padding: [40, 40], maxZoom: 9 });
  }, [chave, map]);

  return null;
}
